import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { AvatarBadge } from '../constants/avatars.jsx';
import { TrophyIcon, StarIcon, CloseIcon, PlusIcon } from './Icons.jsx';
import CountUp from './CountUp.jsx';
import './Leaderboard.css';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function fireCelebration() {
  if (prefersReducedMotion()) return;
  const base = { spread: 70, startVelocity: 42, ticks: 220, zIndex: 120, colors: ['#f6c445', '#1e2a4a', '#3a6df0', '#ffffff'] };
  confetti({ ...base, particleCount: 90, origin: { x: 0.2, y: 0.9 }, angle: 60 });
  confetti({ ...base, particleCount: 90, origin: { x: 0.8, y: 0.9 }, angle: 120 });
  setTimeout(() => confetti({ ...base, particleCount: 60, origin: { x: 0.5, y: 1 } }), 260);
}

/** Animate the "rest" rows to their new positions when the order changes (FLIP). */
function useFlip(deps) {
  const containerRef = useRef(null);
  const prevRects = useRef(new Map());

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || prefersReducedMotion()) return;
    const rows = container.querySelectorAll('[data-flip-id]');
    rows.forEach((row) => {
      const id = row.dataset.flipId;
      const prev = prevRects.current.get(id);
      const next = row.getBoundingClientRect();
      if (prev) {
        const dy = prev.top - next.top;
        if (Math.abs(dy) > 1) {
          row.animate(
            [{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }],
            { duration: 420, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }
          );
        }
      }
      prevRects.current.set(id, next);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return containerRef;
}

export default function Leaderboard({ leaderboard, onClose, isTeacher, onAwardStar, celebrate = false }) {
  const [firstIn, setFirstIn] = useState(false);
  const podium = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);
  const [second, first, third] = [podium[1], podium[0], podium[2]];
  const restRef = useFlip([leaderboard.map((p) => p.id).join(',')]);

  useEffect(() => {
    setFirstIn(false);
    const t = setTimeout(() => setFirstIn(true), 420);
    return () => clearTimeout(t);
  }, [leaderboard.length]);

  useEffect(() => {
    if (celebrate) fireCelebration();
  }, [celebrate]);

  return (
    <div className={`leaderboard-overlay ${celebrate ? 'is-celebration' : ''}`} role="dialog" aria-label="สรุปผลการเรียนรู้">
      <div className="leaderboard-panel card stack">
        <div className="row-between leaderboard-header">
          <div className="row">
            <TrophyIcon size={22} className="leaderboard-trophy-icon" />
            <h2>{celebrate ? 'สรุปผลการเรียนรู้' : 'อันดับดาวสะสม'}</h2>
          </div>
          {onClose && (
            <button className="btn-close" onClick={onClose} aria-label="ปิด">
              <CloseIcon size={20} />
            </button>
          )}
        </div>

        {podium.length > 0 && (
          <div className="podium">
            <PodiumSlot place={2} player={second} visible />
            <PodiumSlot place={1} player={first} visible={firstIn} />
            <PodiumSlot place={3} player={third} visible />
          </div>
        )}

        {rest.length > 0 && (
          <ol className="leaderboard-rest" start={4} ref={restRef}>
            {rest.map((p, idx) => (
              <li key={p.id} data-flip-id={p.id} className="leaderboard-row row-between">
                <div className="row">
                  <span className="leaderboard-rank-num num">{idx + 4}</span>
                  <AvatarBadge avatar={p.avatar} size="sm" />
                  <span className={`leaderboard-name ${p.connected === false ? 'text-dim' : ''}`}>
                    {p.nickname}
                  </span>
                </div>
                <div className="row leaderboard-score-wrap">
                  <span className="row star-tag">
                    <StarIcon size={12} />
                    {p.stars || 0}
                  </span>
                  <strong className="leaderboard-points num">{p.score}</strong>
                  {isTeacher && (
                    <button
                      className="btn-star-award"
                      onClick={() => onAwardStar(p.id)}
                      title={`ให้ดาวสะสมแก่ ${p.nickname}`}
                      aria-label={`ให้ดาวสะสมแก่ ${p.nickname}`}
                    >
                      <PlusIcon size={14} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function PodiumSlot({ place, player, visible }) {
  if (!player) return <div className={`podium-slot podium-slot-${place} is-empty`} />;
  return (
    <div className={`podium-slot podium-slot-${place} ${visible ? 'is-visible' : ''}`}>
      <div className="podium-avatar-wrap">
        <AvatarBadge avatar={player.avatar} size="lg" />
        <span className={`podium-rank-badge rank-${place}`}>{place}</span>
      </div>
      <span className="podium-name" title={player.nickname}>{player.nickname}</span>
      <span className="podium-score num">
        <CountUp value={player.score} /> <span className="podium-pts-label">คะแนน</span>
      </span>
      {(player.stars || 0) > 0 && (
        <span className="podium-stars row">
          <StarIcon size={12} />
          {player.stars}
        </span>
      )}
      <div className="podium-block">
        <span className="podium-pillar-rank num">#{place}</span>
      </div>
    </div>
  );
}
